import type { Messages } from './zh'

/**
 * 英文字典 — 结构必须与 zh.ts 完全一致(Messages 类型强制校验).
 *
 * 来源(旧项目 fastj/client/src/locales/lang/en.json,值逐字复制,不改写):
 * - inx/guideline/subject:完整迁移(旧 en.json 把 等级/评分/未评分/类别 放在顶层,
 *   此处归位到 inx.filter,与 zh 侧对齐)
 * - inx.filter / inx.pager:取自旧 en.json 顶层中文 key 的翻译
 */
const en: Messages = {
  app: {
    login: 'Login',
    logout: 'Logout',
    goLogin: 'go login',
    register: 'Register',
    adminConsole: 'Admin Console',
    check: 'Submit',
    account: 'account',
    password: 'password',
    newPassword: 'new password',
    captcha: 'captcha',
    sendCaptcha: 'send captcha',
  },
  route: {
    home: 'Home',
    login: 'Login',
  },
  // ===== 认证页(P1.2 新增):登录 / 注册 / 找回密码 / 重置密码 =====
  auth: {
    loginTitle: 'Sign in to TRUST',
    loginSubtitle: 'Registry & Evidence-Based Evaluation Platform for Standards',
    account: 'Account',
    accountPlaceholder: 'Username / Email / Phone',
    accountRequired: 'Please enter your account / email / phone',
    password: 'Password',
    passwordRequired: 'Please enter your password',
    passwordPlaceholder: 'Password',
    loginButton: 'Sign In',
    loginFailed: 'Sign in failed',
    toRegister: 'Create an account',
    toForgot: 'Forgot password?',
    backToLogin: 'Back to sign in',
    registerTitle: 'Create a TRUST account',
    username: 'Username',
    usernamePlaceholder: '2-64 chars: letters, digits, _ - .',
    usernameRequired: 'Please enter a username',
    email: 'Email',
    emailPlaceholder: 'Used for password recovery (optional)',
    emailInvalid: 'Invalid email format',
    newPassword: 'New password',
    newPasswordPlaceholder: 'At least 8 chars, with letters and digits',
    confirmPassword: 'Confirm password',
    confirmPasswordRequired: 'Please enter the password again',
    confirmPasswordMismatch: 'The two passwords do not match',
    registerButton: 'Sign Up',
    registerFailed: 'Registration failed',
    forgotTitle: 'Forgot password',
    forgotTip:
      'Enter your username or email. If the account exists, a reset link (valid for 30 minutes) will be sent to its email.',
    forgotButton: 'Send reset link',
    forgotSuccess:
      'If the account exists, a reset link has been sent to its email, valid for 30 minutes',
    resetTitle: 'Reset password',
    resetTokenMissing: 'Invalid link: missing token parameter',
    resetButton: 'Reset password',
    resetSuccess: 'Password has been reset. Please sign in with the new password',
    resetFailed: 'Reset failed',
  },
  inx: {
    title: 'TRUST (Transparent Registry for Unified Standards and Testing)',
    standard: {
      standardRanking: 'Standard Ranking',
    },
    header: {
      home: 'Home Page',
      standardRanking: 'Standard Ranking',
      aboutUs: 'About Us',
      registerStandard: 'Standard Registration',
      standardsLibrary: 'Standards Library',
      myStandard: 'My Standards',
    },
    toRegister: 'To Register',
    aboutUs: {
      title:
        'TRUST (Transparent Registry for Unified Standards and Testing) International Evidence-Based Standards Launch Announcement:',
      subTitle: 'Hello, and welcome to the TRUST International Evidence-Based Standards website.',
      p1: 'TRUST International Evidence-Based Standards are initiated by the Campbell China Network (CCN),Hengshui Jianchen Education and Technology Center. We are dedicated to developing and translating high-quality international evidence-based guidelines and standards in fields such as health and social services. The development of TRUST International Standards strictly follows processes including registration, research, publication, evaluation, and updates, ensuring high-quality control throughout. Our goal is to provide society with trustworthy and actionable high-quality standards and guidelines, guiding both global and Chinese health and social services toward a more equitable, efficient, and sustainable development path.',
      p2: 'CCN, under the guidance of the international Campbell Collaboration network, is composed of over 30 leading evidence-based teams from top universities such as Fudan University, Sichuan University, Wuhan University, Lanzhou University, and Nanjing University of Science and Technology, forming an interdisciplinary academic alliance. We offer standard registration services, provide registration certificates, and display ongoing standard titles on our platform to avoid redundant work. Upon completion of a standard, we will organize professional teams to evaluate the standard and present relevant scores and ratings, assisting you in writing standards more systematically. In our standard retrieval database, we offer the most comprehensive and up-to-date library for your search and reference.',
      p3: 'At the same time, we hope that the TRUST platform can bring more scientific and transparent processes to the standards, along with clearer procedures for standard completion. We will strengthen the role of industry experts in the registration, research, publication, evaluation, and update stages of standards to ensure the fair and just release of standards.',
      p4: 'How to Contact Us:',
      p5: 'After registration, you can contact the administrator via the chat box.',
      privacyTitle: 'Privacy Policy and Terms of Use:',
      ul1: 'I. Privacy Policy',
      ul1l1:
        'When registering a new account on the platform, we will collect your relevant information, including your name, email, address, etc.',
      ul1l2:
        'The information we collect is solely for facilitating standard registration, research, sharing, evaluation, and other related services. ',
      ul1l3: 'We do not share your personal information with third parties.',
      ul1l4: 'You can modify your personal information on the homepage after logging in.',
      ul2: 'II. Terms of Use',
      ul2l1: '1. By using the platform, you agree to the terms outlined herein. ',
      ul2l2: '2. Your responsibilities include: ',
      ul2l2l1:
        '(1) Using the website content legally, without violating any laws or regulations, and refraining from engaging in illegal activities or infringing on others’ rights.',
      ul2l2l2:
        '(2) Providing truthful and accurate information, avoiding false or misleading content. ',
      ul2l2l3:
        '(3) Protecting the security of your account information and password, not disclosing them to third parties, and being responsible for all activities under your account.',
      ul2l2l4:
        '(4) Respecting the website’s intellectual property rights, refraining from unauthorized copying, publishing, or modifying copyrighted content.',
      registerUrl:
        'Click to Register: Global Social Services Standard Guidelines Registration and Evidence Evaluation Platform.',
      evaluation: {
        title: "TRUST's evaluation items for guidelines and standards:",
        item1: {
          title: '一:Is Conflict of Interest Addressed?',
          entry1:
            '1 Record and explain any conflicts of interest during the guideline development process:',
        },
        item2: {
          title: '二:Participation and Representation of Multidisciplinary Team',
          entry1: '1 Ensure comprehensive participation from all relevant disciplines',
        },
        item3: {
          title: '三:Is the Guideline Based on Research Evidence?',
          entry1: '1 Systematic approach to evidence retrieval:',
          entry2: '2 Inclusion and exclusion criteria for evidence:',
          entry3: '3 Grading and rating the quality of evidence:',
          entry4: '4 Recommendation strength system:',
          entry5:
            '5 Consideration of benefits, side effects, and risks when forming recommendations:',
        },
        item4: {
          title: '四:Is Consensus from Multiple Stakeholders Included?',
          entry1:
            '1 Conduct multi-stakeholder consensus during the formation of recommendations to gather opinions:',
        },
        item5: {
          title: '五:Is there a Plan for Dissemination and Updates?',
          entry1: '1 The guideline includes methods for dissemination:',
          entry2: '2 The guideline includes a plan for future updates:',
        },
        flow: {
          title: 'assessment process:',
          content:
            "The platform's sharing standards and guidelines are first evaluated by two assessment experts who rate the text content based on assessment criteria. When the score difference is within three points, the two experts use the platform's dialogue function to communicate and reach a consensus. When the score difference is three points or more, a third assessment expert, who is the team leader, re-evaluates the standards.",
        },
      },
    },
    rank: {
      top100: 'TOP 100 Ranking List',
      field1: 'Ranking',
      field2: 'Title',
      field3: 'Disciplinary classification',
      field4: 'Regulatory body',
      field5: 'Level',
    },
    button: {
      search: 'Search',
    },
    tip: {
      search: 'Please enter a title, fuzzy search is supported',
      sort: 'Sort',
      date: 'Publication Date',
      score: 'Score',
    },
    filter: {
      category: 'Category',
      subject: 'Discipline',
      population: 'Population Classification',
      edition: 'Version',
      level: 'Level',
      score: 'Rating',
      unrated: 'Not rated',
      typeStandard: 'Standard',
      typeGuideline: 'Guidelines',
      popChildren: 'Children',
      popElderly: 'Elderly',
      popWomen: 'Women',
      popTeenagers: 'Teenagers',
      popOther: 'Other populations',
      editionOriginal: 'First edition',
      editionUpdated: 'Updated version',
      filter: 'Filter',
      expand: 'Expand',
      collapse: 'Collapse',
    },
    pager: {
      total: 'Total',
      current: 'Current',
    },
    // ===== 落地页全屏轮播(P0.8 新增,来源旧 inxguide-www 的 inx.www.*,值逐字复制)=====
    landing: {
      joinUs: 'Join us',
      c1Title: 'Standard Registration',
      c1Subtitle: 'Register Your Standards and Gain Global Recognition',
      c1Button: 'Login/Register',
      c2Title: 'Standard Search',
      c2Subtitle: 'Search the Published International Certification Standards Database',
      c2Button: 'Search',
      c3Title: 'Standard Ranking',
      c3Subtitle: 'Published Ranking List',
      c3Button: 'View',
    },
  },
  guideline: {
    subject: 'Discipline',
    journal: 'Published Journal',
    organization: 'Developing Organizations',
    introduction: 'Introduction',
    regNo: 'Registration No',
    regTime: 'Registration Date',
    country: 'Country',
    startDate: 'Start Time',
    endDate: 'End Time',
    fundSource: 'Funding Source',
    originalLink: 'Original Article Link',
    docDownload: 'Document Download',
    ranking: 'Guideline Ranking',
    more: 'More',
    rank: 'Rank',
    level: 'Level',
    score: 'Score',
    certificate: 'Certificate',
    view: 'View',
    none: 'None',
  },
  subject: {
    socialWork: 'Social Sciences - Social Work',
    socialPolicy: 'Social Sciences - Social Policy',
    psychology: 'Social Sciences - Psychology',
    education: 'Social Sciences - Education',
    management: 'Social Sciences - Management',
    economics: 'Social Sciences - Economics',
    socialOther: 'Social Sciences - Other',
    complementaryMedicine: 'Health and Medicine - Complementary and Alternative Medicine',
    publicHealth: 'Health and Medicine - Public Health',
    clinicalMedicine: 'Health and Medicine - Clinical Medicine',
    nursing: 'Health and Medicine - Nursing',
    rehabilitationMedicine: 'Health and Medicine - Rehabilitation Medicine',
    integrativeCare: 'Health and Medicine - Integrative Care',
  },
  // ===== 指南详情页保留能力文案(P0.7 新增):分享/状态机操作弹窗/加载态 =====
  guideDetail: {
    share: 'Share',
    shareCopied: 'Link copied to clipboard',
    copyPrompt: 'Please copy the link below',
    loadFailed: 'Failed to load',
    confirmOp: 'Confirm operation',
    currentStatus: 'Current status',
    remarkPlaceholder: 'Remark (optional); recorded in the flow log',
    opSuccess: 'Operation succeeded',
  },
  // ===== 三态搜索页(P0.3 新增):筛选树父分组名 + 自绘分页器单位文案 =====
  searchPage: {
    groupSocial: 'Social Sciences',
    groupHealth: 'Health and Medicine',
    pageUnit: '',
    perPage: ' / page',
    goto: 'Go to',
    pageSuffix: '',
  },
  // ===== 【P1.9 新增】后台任务中心(/admin/tasks),zh/en 同构 =====
  admin: {
    tasks: {
      title: 'Task Center',
      tabAll: 'All',
      tabTodo: 'To-do',
      tabDone: 'Done',
      colType: 'Type',
      colTitle: 'Title',
      colStatus: 'Status',
      colOperation: 'Operation',
      colTime: 'Time',
      kindGuide: 'Guide',
      kindEvaluation: 'Evaluation',
      kindFlowLog: 'Flow Log',
      bucketTodo: 'To-do',
      bucketDone: 'Done',
      emptyAll: 'No tasks',
      emptyTodo: 'No pending tasks',
      emptyDone: 'No completed records',
      status: {
        Waiting: 'Pending',
        Apply: 'Applied',
        Approved: 'Approved',
        Rejected: 'Rejected',
        Scored: 'Scored',
        Leveled: 'Leveled',
      },
      stage: {
        Register: 'Register',
        Sharing: 'Sharing',
        Evaluation: 'Evaluation',
        Publish: 'Publish',
      },
      operation: {
        submit: 'Submit',
        approve: 'Approve',
        reject: 'Reject',
        resubmit: 'Resubmit',
        request_rectify: 'Request Rectification',
        submit_rectify: 'Submit Rectification',
        final_approve: 'Final Approval',
        system: 'System Transition',
      },
    },
  },
  // ===== P1.4 动态字段配置(新增):注册表单扩展分组回退标题/详情页扩展块/必填校验 =====
  guideFields: {
    sectionTitle: 'Additional Information',
    requiredMsg: 'This field is required',
  },
  // ===== 【P2.6 新增】新建指南查重(需求 #19):相似命中提示弹窗,zh/en 同构 =====
  duplicateCheck: {
    title: 'Similar guides found',
    desc: 'Similar guides below already exist. Please confirm this is not a duplicate registration; you may still proceed.',
    similarity: 'Similarity',
    view: 'View',
    createAnyway: 'Create anyway',
    cancel: 'Go back',
  },
  // ===== 【P3.2 新增】PDF evidence highlight viewer (requirement #54): toolbar/evidence match/loading-error states, zh/en mirrored =====
  pdfViewer: {
    discoveryFailed: 'The local PDF version could not be confirmed. Try again later. No production file was requested.',
    discovering: 'Checking the local PDF version…',
    invalidRequest: 'Invalid PDF location. Use a link with a file version and valid page number.',
    unavailable: 'Local PDF is unavailable or inaccessible. No production file was requested.',
    versionChanged: 'The source version has changed. This cited version cannot be opened. Search for the source again.',
    tooLarge: 'The PDF exceeds the local reader limit of 32 MiB.',
    loadFailed: 'Local PDF loading or verification failed. Please try again later.',
    pageOutOfRange: 'The cited page is outside this PDF version. No alternative page was opened.',
    verifiedRendering: 'Source version verified. Rendering the requested page…',
    verifiedRendered: 'Source version verified. Current page rendered.',
    externalOrigin: 'Visit external source',

    title: 'Original',
    download: 'Download PDF',
    visitOrigin: 'Visit Original',
    noPdf: 'No PDF provided; use "Visit Original" to view',
    loading: 'Loading PDF…',
    loadError: 'Failed to load PDF; try "Download PDF"',
    prevPage: 'Previous',
    nextPage: 'Next',
    pageUnit: 'of',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    evidence: 'Evidence',
    prevMatch: 'Prev match',
    nextMatch: 'Next match',
    matchUnit: 'matches',
    searching: 'Searching evidence quote in document…',
    notFound: 'Evidence quote not found in this PDF',
  },
  // ===== 【P3.1 新增】Detail page AI review tab (requirements #13/#51): five-dimension cards + status/empty states, zh/en mirrored =====
  aiReview: {
    tabInfo: 'Guide Info',
    tabAI: 'AI Review',
    loadFailed: 'Failed to load AI review',
    empty: 'No AI review yet',
    emptyHint: 'This guide has not been AI-reviewed. The five-dimension scores and evidence quotes will appear here once done.',
    running: 'AI review in progress',
    failed: 'AI review failed',
    budgetExceeded: 'Review budget exceeded; please try again later',
    confidence: 'Confidence',
    reasoningSteps: 'Reasoning',
    evidenceQuotes: 'Evidence Quotes',
    riskFlags: 'Risk Flags',
    pagePrefix: 'Around page ',
    pageSuffix: '',
    scoreSuffix: '/ 10',
    notScored: 'Not scored',
    reviewedAt: 'Reviewed at',
    model: 'Model',
    dim: {
      coi: 'Conflict of Interest',
      multidisciplinary: 'Multidisciplinary Involvement',
      evidence: 'Evidence Base',
      consensus: 'Consensus Method',
      dissemination: 'Dissemination & Implementation',
    },
    status: {
      queued: 'Queued',
      parsing: 'Parsing PDF',
      scoring: 'Scoring',
      done: 'Done',
      failed: 'Failed',
      budget_exceeded: 'Budget Exceeded',
    },
  },
  // ===== 【P3.6 新增】Detail page right-rail "Similar Guides" card (requirement #53), zh/en mirrored =====
  similarGuides: {
    title: 'Similar Guides',
  },
}

export default en
